import React from "react";
import { Section, Overline } from "./UI";
import { SEO } from "./SEO";

/* Shared editorial layout for the three legal pages.
 * Uses existing header/footer typography and colour tokens.
 * Renders body copy as text-led paragraphs and bullet lists
 * — deliberately no cards. */

export function LegalPage({ title, effectiveDate, seoTitle, seoDescription, canonical, children }) {
  return (
    <div data-testid="legal-page">
      <SEO title={seoTitle} description={seoDescription} canonical={canonical} />
      {/* Header block */}
      <section className="bg-bone">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <Overline>Legal</Overline>
          <div className="h-px w-12 bg-prestige my-6" />
          <h1
            data-testid="legal-h1"
            className="font-serif italic text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-navy tracking-tight"
          >
            {title}
          </h1>
          <p
            data-testid="legal-effective-date"
            className="mono text-[11px] uppercase tracking-[0.22em] text-prestige mt-6"
          >
            {effectiveDate}
          </p>
        </div>
      </section>

      {/* Body — editorial column, no cards */}
      <Section>
        <div className="max-w-3xl legal-body">
          {children}
        </div>
      </Section>
    </div>
  );
}

/* ---- Reusable content-level primitives ---- */

export function LegalSection({ n, title, children }) {
  return (
    <section className="mt-12 first:mt-0" data-testid={`legal-section-${n}`}>
      <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
        Section {n}
      </div>
      <h2 className="font-serif text-2xl md:text-3xl text-navy mt-2 tracking-tight leading-tight">
        {title}
      </h2>
      <div className="mt-5 space-y-5 text-[15.5px] text-stone2 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export function LegalIntro({ children }) {
  return (
    <div className="text-[16px] text-navy leading-relaxed space-y-5">
      {children}
    </div>
  );
}

export function LegalList({ items }) {
  return (
    <ul className="space-y-2 pl-1" role="list">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-prestige shrink-0" aria-hidden="true" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

export function LegalAddress({ children }) {
  return (
    <address className="not-italic border-l-2 border-prestige/60 pl-4 py-1 text-[15px] text-navy leading-relaxed">
      {children}
    </address>
  );
}
